import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {LoggedInView} from "../common/LoggedInView";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogCheckbox from "../common/form/ElogCheckbox";
import {createCategory, getCategories, resetCreateCategoryStatus} from "../../store/reducers/CategoriesSlice";
import {getCountries} from "../../store/reducers/CountriesSlice";
import CategoryType from "../../models/CategoryType";
import {apiClient} from "../../utils/ApiClient";
import {createClient, resetCreateClientStatus} from "../../store/reducers/ClientsSlice";
import {RequestStatus} from "../../utils/Enums";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useFormik, Field, Form, FormikProvider, useFormikContext} from "formik";
import {createVendor} from "../../store/reducers/VendorsSlice";
import {ESTONIA_ID} from "../../utils/Constants";
import {resetUpdateCategoryTypeStatus} from "../../store/reducers/CategoryTypesSlice";

interface NewClientScreenProps {
}

export interface NewClientFormProps {
    name: string;
    email: string;
    deadline?: string;
    address?: string;
    country: string;
    reg_code?: string;
    vat?: string;
    no_vat: boolean;
    type: string;
    contact_name: string;
    contact_plea: string;
    contact_email: string;
    contact_phone: string;
    groups: string[];
}

export const NewClientScreen: React.FC<NewClientScreenProps> = () => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const clientsState = useAppSelector(state => state.clients);
    const countriesState = useAppSelector(state => state.countries);

    const requestStatus: RequestStatus = clientsState.createClientStatus

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: 'Kliendid', url: '/clients'},
        {text: 'Lisa uus klient', url: '/clients/new'},
    ];

    const typeOptions: Option[] = [
        {value: 'local', label: 'Ettevõte'},
        {value: 'person', label: 'Eraklient'},
        {value: 'foreign', label: 'Välisettevõte'},
        {value: 'foreignPerson', label: 'Välisriigi eraisik'},
    ];

    const languageOptions: Option[] = [
        {value: 'est', label: 'Eesti'},
        {value: 'eng', label: 'Inglise'},
        {value: 'fin', label: 'Soome'},
        //{value: 'swe', label: 'Rootsi'},
    ];

    const [countries, setCountries] = useState<Option[]>([]);
    const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);

    useEffect(() => {
        dispatch(resetCreateClientStatus());
    }, [location]);

    useEffect(() => {

        const countryOptions: Option[] = [];

        countriesState.countries.map(country => {

            const option: Option = {
                value: String(country.id),
                label: country.name
            }

            countryOptions.push(option);

        });

        setCountries(countryOptions);

    }, [countriesState.countries]);

    useEffect(() => {

        dispatch(getCountries());

        apiClient.get("category-types/all-by-category").then((response) => {
            setCategoryTypes(response.data);
        });

    }, []);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            country: ESTONIA_ID,
            type: typeOptions[0]?.value,
            language: 'est'
        },
        onSubmit: (values: any) => {
            dispatch(createClient(values));
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    const getClientData = async () => {
        try {
            // @ts-ignore
            const response = await apiClient.get(`client-info/${values.regCode}`);
            const data = response.data.data;

            setFieldValue('name', data.name);
            setFieldValue('email', data.emails);

            if ( !!data.vatNo ) {
                setFieldValue('vat', data.vatNo);
            } else {
                setFieldValue('noVat', true);
            }

            setFieldValue('address', data.street);
            setFieldValue('postCode', data.postCode);
            setFieldValue('city', data.city);

        } catch (error) {
            //console.error("Error fetching client data:", error);
            // Handle error
        }
    };

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Failed && clientsState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {clientsState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Klient lisatud!</h2>
                        <span>Ära unusta ka kontaktidele staatuseid lisada. <Link to={`/clients/${clientsState?.currentClient?.id}/contacts`}>Vaata kontakte</Link></span>
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2><span>Kliendi andmed</span></h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Ettevõte" name="name" mandatory={true}/>
                            <ElogInput label="Arve E-maili aadress" name="email" mandatory={true}/>
                            <ElogInput label="Maksetähtaeg" name="deadline"/>
                            <ElogInput label="Aadress" name="address"/>
                            <ElogInput label="Linn" name="city"/>
                            <ElogInput label="Postiindeks" name="postCode"/>
                            <ElogSelect label="Riik" name="country" options={countries}/>
                            <ElogInput label="Registrikood" name="regCode" mandatory={true}/>
                            <tr>
                                <th></th>
                                <td><a href="Javascript:;" onClick={() => {getClientData()}}>Võta andmed</a></td>
                            </tr>
                            <ElogInput label="KM number" name="vat"/>
                            <ElogCheckbox label="Pole km kohustlane" name="noVat" />
                            <ElogSelect label="Tüüp" name="type" options={typeOptions}/>
                            </tbody>
                        </table>
                    </div>
                    <div className="form">
                        <h2>
                            <span>Esimene kontaktisik</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Nimi" name="contactName" mandatory={true}/>
                            <ElogInput label="Pöördumine" name="contactPlea" mandatory={true}/>
                            <ElogInput label="E-mail" name="contactEmail" mandatory={true}/>
                            <ElogInput label="Telefon" name="contactPhone" mandatory={true}/>
                            <ElogSelect label="Keel" name="language" options={languageOptions} mandatory={true}/>
                            </tbody>
                        </table>
                    </div>

                    {categoryTypes.map((categoryType) => (
                        <div key={categoryType.id} className="form">
                            <h2>
                                <span>{categoryType.name}</span>
                            </h2>
                            {categoryType?.categories?.map((category) => (
                                <Field
                                    key={category.id}
                                    name="groups"
                                    type="checkbox"
                                    value={String(category.id)}
                                    render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                        <>
                                            <label htmlFor={`group_${category.id}`}>{category.name}</label>
                                            <input
                                                {...props.field}
                                                id={`group_${category.id}`}
                                                type="checkbox" // Set the type attribute to "checkbox"
                                                onChange={(event) => props.field.onChange(event)}
                                                onBlur={() => undefined}
                                            />
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    ))}
                    {requestStatus === RequestStatus.Failed && clientsState.errors.length > 0 && (
                        <div className="message msg-error">
                            <div>
                                <h2>Viga:</h2>
                                {clientsState.errors.map((error, index) => (
                                    <span key={index}>{error}<br/></span>
                                ))}
                            </div>
                        </div>
                    )}
                    {requestStatus === RequestStatus.Success && (
                        <div className="message msg-ok">
                            <div>
                                <h2>Klient lisatud!</h2>
                                <span>Ära unusta ka kontaktidele staatuseid lisada. <Link to={`/clients/${clientsState?.currentClient?.id}/contacts`}>Vaata kontakte</Link></span>
                            </div>
                        </div>
                    )}
                    <div className="action clear">
                        <p>
                            <button type="submit">
                                    <span>
                                      <strong>Sisesta</strong>
                                    </span>
                            </button>
                            <a href="">Tühista</a>
                        </p>
                    </div>
                </Form>
            </FormikProvider>

        </LoggedInView>
    )

}